import { API_BASE_URL } from "./client";

export interface LoginMarketingConfig {
  heading: string;
  description: string;
  heroImageUrl: string;
}

// Matches the login screen's previous hardcoded copy exactly, so a failed or
// slow fetch degrades to today's behavior rather than a blank/broken panel.
export const DEFAULT_LOGIN_MARKETING_CONFIG: LoginMarketingConfig = {
  heading: "Their big match is coming up.",
  description: "Don't miss a beat.",
  heroImageUrl: "/screens/hero.webp",
};

const CACHE_KEY = "loginMarketingConfig";

function isLoginMarketingConfig(value: unknown): value is LoginMarketingConfig {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as LoginMarketingConfig).heading === "string" &&
    typeof (value as LoginMarketingConfig).description === "string" &&
    typeof (value as LoginMarketingConfig).heroImageUrl === "string"
  );
}

/**
 * Read the last successfully-fetched config from localStorage, if any.
 * Used as the login screen's first render so a repeat visitor on the same
 * device sees the real content immediately instead of the hardcoded
 * defaults while the network request is in flight. Never throws — a
 * private-browsing/storage failure just means no cached value.
 */
export function getCachedLoginMarketingConfig(): LoginMarketingConfig | undefined {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return isLoginMarketingConfig(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function cacheLoginMarketingConfig(config: LoginMarketingConfig): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(config));
  } catch {
    // Private browsing / storage full / disabled — caching is a nice-to-have.
  }
}

export const loginMarketingConfigApi = {
  /**
   * Public, unauthenticated — shown on the login screen before any session
   * exists. Deliberately not routed through makeRequest in ./client: that
   * function's job is the token-refresh-on-401 dance, which doesn't apply
   * to a public endpoint.
   */
  getConfig: async (): Promise<LoginMarketingConfig> => {
    const response = await fetch(
      `${API_BASE_URL}/v1/public/login-marketing-config`
    );
    if (!response.ok) {
      throw new Error(
        `Failed to load login marketing config: ${response.status}`
      );
    }
    const config = await response.json();
    cacheLoginMarketingConfig(config);
    return config;
  },
};
